import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Table } from "../../../components/SystemComponent/table";
import { GroupButton } from "../../../components/SystemComponent/GroupButton";
import { Badge } from "../../../components/SystemComponent/Bagde";
import { Modal } from "../../../components/SystemComponent/Modal";
import { Button } from "../../../components/SystemComponent/Button";
import { MdAdd, MdEdit, MdCheck, MdClose } from "react-icons/md";
import { createApi } from "../../../../core/api";

/* ---------------- CONST ---------------- */

const ConfirmStatusText = { 0: "Pending", 1: "Approved", 2: "Rejected" };
const ConfirmStatusColor = { 0: "#F59E0B", 1: "#16A34A", 2: "#DC2626" };
const ModuleTypeText = { 1: "Core", 2: "Extra" };
const ModuleTypeStyle = {
  1: { color: "#2563EB", bg: "rgba(37,99,235,.15)" },
  2: { color: "#ee0ad0ff", bg: "rgba(249,115,22,.18)" },
};

const selectStyle = {
  width: "100%",
  height: 36,
  borderRadius: 8,
  border: "1px solid #CBD5E1",
  padding: "0 10px",
  marginBottom: 10,
};

const label = {
  fontSize: 13,
  fontWeight: 600,
  color: "#334155",
  marginBottom: 4,
  display: "block",
};

/* ---------------- STATUS MODAL ---------------- */

const ConfirmStatusModal = ({ row, onClose, onSaved }: any) => {
  const api = useMemo(() => createApi(), []);
  const [status, setStatus] = useState(row.confirmStatus);

  const save = async () => {
    await api.companyModuleSubscription.updateConfirmStatus(row.id, status);
    onClose();
    onSaved();
  };

  return (
    <Modal open onClose={onClose}>
      <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 14 }}>Update Confirm Status</div>
      <label>Confirm Status</label>
      <select
        value={status}
        onChange={(e) => setStatus(+e.target.value)}
        style={selectStyle}
      >
        <option value={0}>Pending</option>
        <option value={1}>Approved</option>
        <option value={2}>Rejected</option>
      </select>

      <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
        <Button icon={MdCheck} onClick={save}>
          Confirm
        </Button>
        <Button icon={MdClose} variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
};

/* ---------------- CREATE MODAL (ROW) ---------------- */

const CreateSubscriptionModal = ({ companyId, onClose, onSaved }: any) => {
  const api = useMemo(() => createApi(), []);
  const [modules, setModules] = useState<any[]>([]);
  const [packages, setPackages] = useState<any[]>([]);
  const [moduleType, setModuleType] = useState(1);
  const [moduleId, setModuleId] = useState<number>();
  const [packageId, setPackageId] = useState<number>();
  const [confirm, setConfirm] = useState(false);

  useEffect(() => {
    api.module.getAll().then((r: any) => setModules(r?.data?.data ?? r?.data ?? []));
  }, []);

  useEffect(() => {
    if (moduleId)
      api.package
        .getByModuleId(moduleId)
        .then((r: any) => setPackages(r?.data?.data ?? r?.data ?? []));
    else setPackages([]);
  }, [moduleId]);

  const create = async () => {
    if(!moduleId) return;
    const module: any = await api.module.getById(moduleId);
    if (!module?.ok) return;

    await api.companyModuleSubscription.create({
      companyId,
      moduleId: module.data.data.id,
      packageId,
    });

    onClose();
    onSaved();
  };

  if (confirm)
    return (
      <Modal open onClose={onClose}>
        <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 14 }}>Confirm Subscription</div>
        <div>Are you sure?</div>
        <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
          <Button onClick={create}>Yes</Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </Modal>
    );

  return (
    <Modal open onClose={onClose}>
      <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 14 }}>Create Subscription</div>
      <label style={label}>Module Type</label>
      <select
        value={moduleType}
        onChange={(e) => setModuleType(+e.target.value)}
        style={selectStyle}
      >
        <option value={1}>Core</option>
        <option value={2}>Extra</option>
      </select>

      <label style={label}>Module</label>
      <select
        onChange={(e) => setModuleId(+e.target.value)}
        style={selectStyle}
      >
        <option value="">Select Module</option>
        {modules
          .filter((x) => x.moduleType === moduleType)
          .map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
      </select>

      <label style={label}>Package</label>
      <select
        disabled={!packages.length}
        onChange={(e) => setPackageId(+e.target.value)}
        style={{
          ...selectStyle,
          background: packages.length ? "#fff" : "#F1F5F9",
        }}
      >
        <option value="">Select Package</option>
        {packages.map((p) => (
          <option key={p.id} value={p.id}>
            {p.title}
          </option>
        ))}
      </select>

      <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
        <Button
          disabled={!moduleId || !packageId}
          onClick={() => setConfirm(true)}
        >
          Create
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
};

/* ---------------- CREATE MODAL (GLOBAL) ---------------- */

const CreateSubscriptionForAnyCompanyModal = ({ onClose, onSaved }: any) => {
  const api = useMemo(() => createApi(), []);
  const [companies, setCompanies] = useState<any[]>([]);
  const [modules, setModules] = useState<any[]>([]);
  const [packages, setPackages] = useState<any[]>([]);
  const [companyId, setCompanyId] = useState<number>();
  const [moduleType, setModuleType] = useState(1);
  const [moduleId, setModuleId] = useState<number>();
  const [packageId, setPackageId] = useState<number>();
  const [confirm, setConfirm] = useState(false);

  useEffect(() => {
    api.company.getAll().then((r: any) => setCompanies(r?.data?.data ?? r?.data ?? []));
    api.module.getAll().then((r: any) => setModules(r?.data?.data ?? r?.data ?? []));
  }, []);

  useEffect(() => {
    if (moduleId)
      api.package
        .getByModuleId(moduleId)
        .then((r: any) => setPackages(r?.data?.data ?? r?.data ?? []));
    else setPackages([]);
  }, [moduleId]);

  const create = async () => {
    if(!moduleId) return;
    const module: any = await api.module.getById(moduleId);
    if (!module?.ok) return;

    await api.companyModuleSubscription.create({
      companyId,
      moduleId: module.data.data.id,
      packageId,
    });

    onClose();
    onSaved();
  };

  if (confirm)
    return (
      <Modal open onClose={onClose}>
        <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 14 }}>Confirm Subscription</div>
        <div>Are you sure?</div>
        <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
          <Button onClick={create}>Yes</Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </Modal>
    );

  return (
    <Modal open onClose={onClose}>
      <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 14 }}>Add Subscription</div>
      <label style={label}>Select Company</label>
      <select
        onChange={(e) => setCompanyId(+e.target.value)}
        style={selectStyle}
      >
        <option value="">Select Company</option>
        {companies.map((c) => (
          <option key={c.id} value={c.id}>
            {c.companyName}
          </option>
        ))}
      </select>
      <label style={label}>Select Module Type</label>
      <select
        value={moduleType}
        onChange={(e) => setModuleType(+e.target.value)}
        style={selectStyle}
      >
        <option value={1}>Core</option>
        <option value={2}>Extra</option>
      </select>

      <label style={label}>Select Module</label>
      <select
        onChange={(e) => setModuleId(+e.target.value)}
        style={selectStyle}
      >
        <option value="">Select Module</option>
        {modules
          .filter((x) => x.moduleType === moduleType)
          .map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
      </select>
      <label style={label}>Select Package</label>
      <select
        disabled={!packages.length}
        onChange={(e) => setPackageId(+e.target.value)}
        style={{
          ...selectStyle,
          background: packages.length ? "#fff" : "#F1F5F9",
        }}
      >
        <option value="">Select Package</option>
        {packages.map((p) => (
          <option key={p.id} value={p.id}>
            {p.title}
          </option>
        ))}
      </select>

      <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
        <Button
          disabled={!companyId || !moduleId || !packageId}
          onClick={() => setConfirm(true)}
        >
          Create
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
};

/* ---------------- PAGE ---------------- */

export const CompanySubscriptionManagement = () => {
  const api = useMemo(() => createApi(), []);
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [openGlobalCreate, setOpenGlobalCreate] = useState(false);
  const [openRowCreate, setOpenRowCreate] = useState(false);
  const [openStatus, setOpenStatus] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const r: any = await api.companyModuleSubscription.getAll();
    setData(r?.data?.data ?? []);
    setLoading(false);
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo<any[]>(
    () => [
      {
        key: "company",
        title: "Company",
        accessor: (r: any) => r.company.companyName,
      },
      { key: "module", title: "Module", accessor: (r: any) => r.module.name },
      {
        key: "isAllInPackage",
        title: "All In One Module",
        type: "boolean",
        accessor: (r: any) => r.isAllInPackage,
        render: (r: any, v: boolean) =>
          v ? (
            <span style={{ color: "#16a34a", fontWeight: 600 }}>✔</span>
          ) : (
            <span style={{ color: "red", fontWeight: 600 }}>✖</span>
          ),
      },

      {
        key: "type",
        title: "Type",
        accessor: (r: any) => r.module.moduleType,
        render: (r: any, v: number) => {
          const s = (ModuleTypeStyle as any)[v];
          return <Badge label={(ModuleTypeText as any)[v]} color={s.color} bg={s.bg} />;
        },
      },
      {
        key: "package",
        title: "Package",
        accessor: (r: any) => r.package.title,
      },
      {
        key: "price",
        title: "Price",
        accessor: (r: any) => r.price.toFixed(2),
      },
      {
        key: "status",
        title: "Status",
        accessor: (r: any) => r.confirmStatus,
        render: (r: any, v: number) => (
          <Badge
            label={(ConfirmStatusText as any)[v]}
            color={(ConfirmStatusColor as any)[v]}
          />
        ),
      },
      {
        key: "actions",
        title: "Actions",
        render: (r: any) => (
          <GroupButton
            items={[
              {
                key: "status",
                icon: MdEdit,
                color: "#2563EB",
                onClick: () => {
                  setSelectedRow(r);
                  setOpenStatus(true);
                },
              },
              {
                key: "create",
                icon: MdAdd,
                color: "#16A34A",
                onClick: () => {
                  setSelectedRow(r);
                  setOpenRowCreate(true);
                },
              },
            ]}
          />
        ),
      },
    ],
    [],
  );

  return (
    <div style={{ padding: 20 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: 12,
        }}
      >
        <Button icon={MdAdd} onClick={() => setOpenGlobalCreate(true)}>
          Add Subscription
        </Button>
      </div>

      <Table loading={loading} columns={columns} data={data} />

      {openStatus && selectedRow && (
        <ConfirmStatusModal
          row={selectedRow}
          onClose={() => setOpenStatus(false)}
          onSaved={load}
        />
      )}
      {openRowCreate && selectedRow && (
        <CreateSubscriptionModal
          companyId={selectedRow.company.id}
          onClose={() => setOpenRowCreate(false)}
          onSaved={load}
        />
      )}
      {openGlobalCreate && (
        <CreateSubscriptionForAnyCompanyModal
          onClose={() => setOpenGlobalCreate(false)}
          onSaved={load}
        />
      )}
    </div>
  );
};
